import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import { Box, Typography, Chip, Card, CardContent, Grid, IconButton, Tooltip } from '@mui/material';
import { 
  CellTower as TowerIcon, 
  SignalCellular4Bar as SignalIcon,
  Build as MaintenanceIcon,
  Error as OfflineIcon,
  Refresh as RefreshIcon,
  ZoomIn as ZoomInIcon
} from '@mui/icons-material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { procogiaBrand } from '../theme/procogia';

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface NetworkTower {
  tower_id: string;
  name: string;
  city: string;
  state: string;
  location: {
    lat: number;
    lng: number;
  };
  status: 'operational' | 'maintenance' | 'offline';
  technology: string;
  capacity: number;
  currentLoad: number;
  lastMaintenance: string;
  coverageRadius?: number;
}

interface NetworkMapProps {
  towers?: NetworkTower[];
  height?: string;
  showCoverage?: boolean;
  enableClustering?: boolean;
}

const NetworkMap: React.FC<NetworkMapProps> = ({ 
  towers = [], 
  height = '600px',
  showCoverage = false,
  enableClustering = false 
}) => {
  const [mapTowers, setMapTowers] = useState<NetworkTower[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTower, setSelectedTower] = useState<NetworkTower | null>(null);

  // Create custom icons for different tower statuses
  const createTowerIcon = (status: string) => {
    const colors = {
      operational: procogiaBrand.auth0.success, // ProCogia Green
      maintenance: procogiaBrand.status.warning, // ProCogia Blue
      offline: procogiaBrand.auth0.error        // ProCogia Blue for error
    };

    const color = colors[status as keyof typeof colors] || procogiaBrand.gray.light;

    return L.divIcon({
      html: `
        <div style="
          background: ${color};
          border: 3px solid white;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          position: relative;
        ">
          <div style="
            width: 8px;
            height: 8px;
            background: white;
            border-radius: 50%;
          "></div>
          ${status === 'operational' ? `
            <div style="
              position: absolute;
              top: -5px;
              right: -5px;
              width: 12px;
              height: 12px;
              background: ${color};
              border: 2px solid white;
              border-radius: 50%;
              animation: pulse 2s infinite;
            "></div>
          ` : ''}
        </div>
        <style>
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.2); opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
        </style>
      `,
      className: 'custom-tower-icon',
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12]
    });
  };

  // Mock data - in real app this would come from API
  useEffect(() => {
    const loadTowerData = async () => {
      setIsLoading(true);
      
      // Use provided towers or load mock data
      if (towers.length > 0) {
        setMapTowers(towers);
      } else {
        // Mock network towers across major US cities
        const mockTowers: NetworkTower[] = [
          // New York
          { tower_id: 'NY-001', name: 'Manhattan Central', city: 'New York', state: 'NY', 
            location: { lat: 40.7589, lng: -73.9851 }, status: 'operational', technology: '5G', 
            capacity: 8500, currentLoad: 65.2, lastMaintenance: '2024-06-15', coverageRadius: 5.2 },
          { tower_id: 'NY-002', name: 'Brooklyn Heights', city: 'New York', state: 'NY',
            location: { lat: 40.6892, lng: -73.9442 }, status: 'operational', technology: '5G',
            capacity: 7200, currentLoad: 73.8, lastMaintenance: '2024-06-10', coverageRadius: 4.8 },
          { tower_id: 'NY-003', name: 'Queens Plaza', city: 'New York', state: 'NY',
            location: { lat: 40.7505, lng: -73.9375 }, status: 'maintenance', technology: '4G LTE',
            capacity: 6100, currentLoad: 42.1, lastMaintenance: '2024-06-20', coverageRadius: 4.2 },

          // Los Angeles  
          { tower_id: 'LA-001', name: 'Downtown LA', city: 'Los Angeles', state: 'CA',
            location: { lat: 34.0522, lng: -118.2437 }, status: 'operational', technology: '5G',
            capacity: 9200, currentLoad: 78.5, lastMaintenance: '2024-06-12', coverageRadius: 6.1 },
          { tower_id: 'LA-002', name: 'Hollywood Hills', city: 'Los Angeles', state: 'CA',
            location: { lat: 34.1341, lng: -118.3215 }, status: 'operational', technology: '5G',
            capacity: 7800, currentLoad: 56.3, lastMaintenance: '2024-06-08', coverageRadius: 5.5 },
          { tower_id: 'LA-003', name: 'Santa Monica', city: 'Los Angeles', state: 'CA',
            location: { lat: 34.0195, lng: -118.4912 }, status: 'offline', technology: '4G LTE',
            capacity: 5400, currentLoad: 0, lastMaintenance: '2024-05-28', coverageRadius: 3.8 },

          // Chicago
          { tower_id: 'CHI-001', name: 'Loop District', city: 'Chicago', state: 'IL',
            location: { lat: 41.8781, lng: -87.6298 }, status: 'operational', technology: '5G',
            capacity: 8100, currentLoad: 68.7, lastMaintenance: '2024-06-18', coverageRadius: 4.9 },
          { tower_id: 'CHI-002', name: 'North Shore', city: 'Chicago', state: 'IL',
            location: { lat: 41.9278, lng: -87.6569 }, status: 'operational', technology: '5G',
            capacity: 7300, currentLoad: 71.2, lastMaintenance: '2024-06-14', coverageRadius: 5.1 },

          // Houston
          { tower_id: 'HOU-001', name: 'Energy Corridor', city: 'Houston', state: 'TX',
            location: { lat: 29.7604, lng: -95.3698 }, status: 'operational', technology: '5G',
            capacity: 8800, currentLoad: 64.9, lastMaintenance: '2024-06-16', coverageRadius: 5.8 },
          { tower_id: 'HOU-002', name: 'Medical Center', city: 'Houston', state: 'TX',
            location: { lat: 29.7066, lng: -95.3978 }, status: 'maintenance', technology: '5G',
            capacity: 7600, currentLoad: 45.2, lastMaintenance: '2024-06-22', coverageRadius: 4.7 },

          // Phoenix
          { tower_id: 'PHX-001', name: 'Downtown Phoenix', city: 'Phoenix', state: 'AZ',
            location: { lat: 33.4484, lng: -112.0740 }, status: 'operational', technology: '5G',
            capacity: 7900, currentLoad: 62.4, lastMaintenance: '2024-06-11', coverageRadius: 6.2 },

          // Atlanta
          { tower_id: 'ATL-001', name: 'Midtown Atlanta', city: 'Atlanta', state: 'GA',
            location: { lat: 33.7490, lng: -84.3880 }, status: 'operational', technology: '5G',
            capacity: 8300, currentLoad: 69.1, lastMaintenance: '2024-06-13', coverageRadius: 5.3 },

          // Miami
          { tower_id: 'MIA-001', name: 'South Beach', city: 'Miami', state: 'FL',
            location: { lat: 25.7617, lng: -80.1918 }, status: 'operational', technology: '5G',
            capacity: 7100, currentLoad: 82.3, lastMaintenance: '2024-06-09', coverageRadius: 4.1 },

          // Seattle
          { tower_id: 'SEA-001', name: 'Capitol Hill', city: 'Seattle', state: 'WA',
            location: { lat: 47.6062, lng: -122.3321 }, status: 'operational', technology: '5G',
            capacity: 6800, currentLoad: 58.7, lastMaintenance: '2024-06-17', coverageRadius: 4.6 },

          // Denver
          { tower_id: 'DEN-001', name: 'LoDo District', city: 'Denver', state: 'CO',
            location: { lat: 39.7392, lng: -104.9903 }, status: 'operational', technology: '5G',
            capacity: 7400, currentLoad: 55.9, lastMaintenance: '2024-06-19', coverageRadius: 5.7 }
        ];
        
        setMapTowers(mockTowers);
      }
      
      setIsLoading(false);
    };

    loadTowerData();
  }, [towers]);

  const getStatusColor = (status: string) => {
    const colors = {
      operational: procogiaBrand.auth0.success,
      maintenance: procogiaBrand.status.warning, 
      offline: procogiaBrand.auth0.error
    };
    return colors[status as keyof typeof colors] || procogiaBrand.gray.light;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational': return <SignalIcon sx={{ fontSize: 16, color: procogiaBrand.auth0.success }} />;
      case 'maintenance': return <MaintenanceIcon sx={{ fontSize: 16, color: procogiaBrand.status.warning }} />;
      case 'offline': return <OfflineIcon sx={{ fontSize: 16, color: procogiaBrand.auth0.error }} />;
      default: return <TowerIcon sx={{ fontSize: 16, color: procogiaBrand.gray.light }} />;
    }
  };

  const getTechnologyColor = (tech: string) => {
    switch (tech) {
      case '5G': return procogiaBrand.green.main;
      case '4G LTE': return procogiaBrand.blue.main;
      case '3G': return procogiaBrand.gray.light;
      default: return procogiaBrand.gray.light;
    }
  };

  // Calculate network statistics
  const totalTowers = mapTowers.length;
  const operationalTowers = mapTowers.filter(t => t.status === 'operational').length;
  const maintenanceTowers = mapTowers.filter(t => t.status === 'maintenance').length;
  const offlineTowers = mapTowers.filter(t => t.status === 'offline').length;
  const averageLoad = mapTowers.reduce((sum, t) => sum + t.currentLoad, 0) / totalTowers;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Network Statistics Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ color: '#E5E7EB', fontWeight: 600 }}>
            🗺️ US Network Coverage Map
          </Typography>
          <Box display="flex" gap={1}>
            <Tooltip title="Show Coverage Areas">
              <IconButton 
                size="small" 
                sx={{ color: showCoverage ? '#95c100' : '#6B7280' }}
              >
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh Data">
              <IconButton size="small" sx={{ color: '#6B7280' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Box textAlign="center">
              <Typography variant="h4" sx={{ color: '#95c100', fontWeight: 'bold' }}>
                {operationalTowers}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                Operational
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box textAlign="center">
              <Typography variant="h4" sx={{ color: '#95c100', fontWeight: 'bold' }}>
                {maintenanceTowers}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                Maintenance
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box textAlign="center">
              <Typography variant="h4" sx={{ color: '#e74c3c', fontWeight: 'bold' }}>
                {offlineTowers}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                Offline
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box textAlign="center">
              <Typography variant="h4" sx={{ color: '#1fb8cc', fontWeight: 'bold' }}>
                {averageLoad.toFixed(1)}%
              </Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                Avg Load
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Map Container */}
      <Box sx={{ flexGrow: 1, position: 'relative', height }}>
        {isLoading ? (
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center" 
            height="100%"
            sx={{ background: 'linear-gradient(135deg, #1fb8cc 0%, #1fb8cc 100%)' }}
          >
            <Typography sx={{ color: '#E5E7EB' }}>Loading network map...</Typography>
          </Box>
        ) : (
          <MapContainer
            center={[39.8283, -98.5795]} // Geographic center of US
            zoom={4}
            style={{ height: '100%', width: '100%' }}
            zoomControl={true}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            
            {mapTowers.map((tower) => (
              <React.Fragment key={tower.tower_id}>
                <Marker
                  position={[tower.location.lat, tower.location.lng]}
                  icon={createTowerIcon(tower.status)}
                  eventHandlers={{
                    click: () => setSelectedTower(tower)
                  }}
                >
                  <Popup>
                    <Card sx={{ minWidth: 280, background: 'rgba(17, 24, 39, 0.95)', color: '#E5E7EB' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          {getStatusIcon(tower.status)}
                          <Typography variant="h6" sx={{ color: '#F3F4F6', fontWeight: 600 }}>
                            {tower.name}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" gap={1} mb={2}>
                          <Chip 
                            label={tower.status.toUpperCase()} 
                            size="small"
                            sx={{ 
                              backgroundColor: getStatusColor(tower.status),
                              color: 'white',
                              fontSize: '0.75rem',
                              height: 20
                            }}
                          />
                          <Chip 
                            label={tower.technology} 
                            size="small"
                            sx={{ 
                              backgroundColor: getTechnologyColor(tower.technology),
                              color: 'white',
                              fontSize: '0.75rem',
                              height: 20
                            }}
                          />
                        </Box>

                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                              Location
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#E5E7EB' }}>
                              {tower.city}, {tower.state}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                              Capacity
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#E5E7EB' }}>
                              {tower.capacity.toLocaleString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                              Current Load
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#E5E7EB' }}>
                              {tower.currentLoad.toFixed(1)}%
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                              Last Maintenance
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#E5E7EB' }}>
                              {tower.lastMaintenance}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Popup>
                </Marker>

                {/* Coverage Area Circles */}
                {showCoverage && tower.coverageRadius && (
                  <Circle
                    center={[tower.location.lat, tower.location.lng]}
                    radius={tower.coverageRadius * 1000} // Convert km to meters
                    fillColor={getStatusColor(tower.status)}
                    fillOpacity={0.1}
                    color={getStatusColor(tower.status)}
                    weight={1}
                    opacity={0.3}
                  />
                )}
              </React.Fragment>
            ))}
          </MapContainer>
        )}
      </Box>
    </Box>
  );
};

export default NetworkMap; 